import React, { useEffect, useState } from "react";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SailingOutlinedIcon from "@mui/icons-material/SailingOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { usePagesTranslation } from "../../locales";
import { wwPlayColorsLight } from "./theme";
import WordWheelPackShop from "./WordWheelPackShop";
import WordWheelService from "./WordWheelService";
import WordWheelWallet from "./WordWheelWallet";
import { getGuestJourneyLevel } from "./constants";
import { resolvePlayBackground } from "./bgAssets";
import wordWheelQuestIcon from "../../../assets/app-icons/word-wheel-quest-app-icon.png";
import {
    SHOW_WORD_WHEEL_APP_STORE_BADGE,
    SHOW_WORD_WHEEL_GOOGLE_PLAY_BADGE,
    WORD_WHEEL_QUEST_APP_STORE_URL,
    WORD_WHEEL_QUEST_GOOGLE_PLAY_URL,
} from "../../../config/nativeAppsVisibility";
import AppStoreBadgeLink from "../../components/AppStoreBadgeLink";
import AppStorePromoPill from "../../components/AppStorePromoPill";
import GooglePlayBadgeLink from "../../components/GooglePlayBadgeLink";

function JourneyCard({ level, onClick }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                width: "100%",
                m: 0,
                mb: 1.5,
                pt: 4.5,
                pb: 2,
                px: 2,
                minHeight: 128,
                borderRadius: 2,
                border: "1px solid rgba(125, 211, 252, 0.4)",
                bgcolor: "rgba(15, 55, 75, 0.72)",
                cursor: "pointer",
                textAlign: "left",
                color: "#f0f9ff",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    minHeight: 28,
                    borderRadius: 999,
                    bgcolor: "#fbbf24",
                    color: "#0c4a6e",
                    fontWeight: 800,
                    fontSize: "0.8rem",
                    lineHeight: 1,
                }}
            >
                <CardGiftcardIcon sx={{ fontSize: 16 }} />
                Free
            </Box>
            <Box
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: "rgba(56, 189, 248, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 1.5,
                    flexShrink: 0,
                }}
            >
                <SailingOutlinedIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", lineHeight: 1.2 }}>Journey</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Level {level}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.95 }}>
                    The whole season is open. Play every level, with nothing to buy.
                </Typography>
            </Box>
            <ChevronRightIcon />
        </Box>
    );
}

export default function WordWheelHubPage() {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const colors = wwPlayColorsLight;
    const [loading, setLoading] = useState(true);
    const [signedIn, setSignedIn] = useState(false);
    const [level, setLevel] = useState(1);
    const [ownedCodes, setOwnedCodes] = useState(() => new Set());

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const loggedIn = await WordWheelService.isLoggedIn();
                if (cancelled) return;
                setSignedIn(loggedIn);
                if (loggedIn) {
                    const data = await WordWheelService.fetchNext();
                    const n = Number(data?.mainJourneyLevel ?? data?.puzzleLevel);
                    if (Number.isFinite(n) && n >= 1) setLevel(n);
                    try {
                        const owned = await WordWheelService.fetchOwnedPacks();
                        if (!cancelled) setOwnedCodes(new Set(owned));
                    } catch {
                        /* packs stay locked until ownership can be read */
                    }
                } else {
                    setLevel(getGuestJourneyLevel());
                    setOwnedCodes(new Set());
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const sceneBg = resolvePlayBackground();

    const mobileApps = SHOW_WORD_WHEEL_APP_STORE_BADGE || SHOW_WORD_WHEEL_GOOGLE_PLAY_BADGE ? (
        <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: 800, mb: 1.5, color: "#f0f9ff" }}>
                Android and iPhone
            </Typography>
            <AppStorePromoPill
                variant="hero"
                iconSrc={wordWheelQuestIcon}
                appName="Word Wheel Quest"
                tagline={t("marketing.wordWheelQuest.subtitle")}
                sx={{ flexWrap: "wrap", justifyContent: "flex-start", borderRadius: 3, width: "100%" }}
            >
                {SHOW_WORD_WHEEL_APP_STORE_BADGE ? (
                    <AppStoreBadgeLink
                        height={44}
                        href={WORD_WHEEL_QUEST_APP_STORE_URL}
                        ariaLabel={t("landing.word-wheel-app-store-badge-aria")}
                    />
                ) : null}
                {SHOW_WORD_WHEEL_GOOGLE_PLAY_BADGE ? (
                    <GooglePlayBadgeLink
                        height={44}
                        href={WORD_WHEEL_QUEST_GOOGLE_PLAY_URL}
                        ariaLabel={t("landing.word-wheel-google-play-badge-aria")}
                    />
                ) : null}
            </AppStorePromoPill>
        </Box>
    ) : null;

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, md: 4 },
                px: { xs: 2, md: 4 },
                bgcolor: "#031525",
            }}
        >
            <Box
                sx={{
                    maxWidth: 1180,
                    mx: "auto",
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "minmax(340px, 460px) minmax(0, 1fr)" },
                    gap: { xs: 3, md: 4 },
                    alignItems: "start",
                }}
            >
            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    minHeight: { xs: 520, md: 640 },
                    p: { xs: 2.5, sm: 3.5 },
                    borderRadius: 3,
                    bgcolor: "rgba(8, 47, 73, 0.12)",
                    color: "#f0f9ff",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid rgba(186, 230, 253, 0.55)",
                    boxShadow: "0 12px 40px rgba(3, 21, 37, 0.38)",
                }}
            >
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "inherit",
                        overflow: "hidden",
                        zIndex: 0,
                        pointerEvents: "none",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundImage: sceneBg.url ? `url("${sceneBg.url}")` : "none",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            opacity: 0.48,
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "rgba(3, 21, 37, 0.52)",
                        }}
                    />
                </Box>
                <Box sx={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", flex: 1 }}>
                <Typography variant="overline" sx={{ letterSpacing: 2, fontWeight: 700 }}>
                    {t("wordwheel.kicker")}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                    {t("wordwheel.title")}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                    {t("wordwheel.hub-intro")}
                </Typography>
                <WordWheelWallet />
                <Box sx={{ flex: 1, minHeight: 16 }} />
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress sx={{ color: colors.accent }} />
                    </Box>
                ) : (
                    <Box sx={{ mt: 2 }}>
                        <JourneyCard
                            level={level}
                            onClick={() => navigate("/puzzles/word-wheel/journey")}
                        />
                    </Box>
                )}
                <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                    <Button component={RouterLink} to="/howto/puzzle/word-wheel" sx={{ color: "#bae6fd" }}>
                        {t("wordwheel.howto-link")}
                    </Button>
                </Stack>
                </Box>
            </Paper>
            <Box sx={{ minWidth: 0, pt: { md: 1 } }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, color: "#f0f9ff" }}>
                    Packs
                </Typography>
                {loading ? null : (
                    <WordWheelPackShop signedIn={signedIn} ownedCodes={ownedCodes} />
                )}
                {mobileApps}
            </Box>
            </Box>
        </Box>
    );
}
